import { ReactNode, useEffect, useRef } from 'react';

import { selectUser } from 'store';

import Loader from 'shared/Loader';

import { isBootstrapQuerySettled, useAuthBootstrapStatus } from 'contexts/authBootstrapContext';
import { useAppSelector } from 'hooks';
import { useCompleteProfile } from 'hooks/useCompleteProfile';
import { useMyPrograms } from 'hooks/useMyPrograms/useMyPrograms';
import { getStardustPostOnboardingUrl } from 'utils/stardustPostOnboarding';

import type { ProgramCategory } from 'models/programs.types';

interface StardustPostOnboardingGuardProps {
  /** Restricts the hand-off to this program, for routes that belong to a single program. */
  category?: ProgramCategory;
  children: ReactNode;
}

const StardustPostOnboardingGuard = ({ category, children }: StardustPostOnboardingGuardProps) => {
  const { accessToken, identityVerified } = useAppSelector(selectUser);
  const { activePrograms = [] } = useMyPrograms();
  const { myAccount, myPrograms } = useAuthBootstrapStatus();
  const { filteredKeys, isReady: isProfileReady } = useCompleteProfile(category ?? 'any');
  const hasRedirected = useRef(false);

  const handoffUrl = getStardustPostOnboardingUrl({ accessToken, activePrograms, category });

  // Stardust owns every post-onboarding step, so it takes over whenever the portal would still
  // flag one. ID is checked separately, as the "Verify ID" banner does, because
  // `useCompleteProfile` only counts it on the Zoom call route.
  const isOnboardingIncomplete = filteredKeys.length > 0 || !identityVerified;

  // The user slice defaults to unverified with no pharmacy until /my-account first returns,
  // which would send every Stardust patient to the hand-off. Only the first load matters:
  // refetches keep the previous data, so the portal flow is never unmounted mid-step.
  // If that first load fails, the profile can never become ready (no `userId`), and the
  // defaults can't be trusted to hand off, so fall back to the portal flow.
  const isAccountFailed = myAccount.isError && !myAccount.isSuccess;
  const isProfilePending =
    !!handoffUrl && !isAccountFailed && (!myAccount.isSuccess || !isProfileReady);
  const isProgramsPending = !activePrograms.length && !isBootstrapQuerySettled(myPrograms);
  const shouldRedirect =
    !!handoffUrl && !isAccountFailed && !isProfilePending && isOnboardingIncomplete;

  useEffect(() => {
    if (!shouldRedirect || !handoffUrl || hasRedirected.current) return;
    hasRedirected.current = true;
    // `replace`, not `assign`: this route would bounce Back straight to Stardust again.
    window.location.replace(handoffUrl);
  }, [handoffUrl, shouldRedirect]);

  // Rendering the in-portal intake before programs and profile settle would flash it ahead of
  // the hand-off for patients whose program has a Stardust flow.
  if (shouldRedirect || isProfilePending || isProgramsPending) {
    return <Loader isVisible />;
  }

  return <>{children}</>;
};

export default StardustPostOnboardingGuard;
